/**
 * @file RepLapsedAccountsWidget.js
 * @version 1.4.1
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-09-03
 * @modified 2026-10-08
 *
 * Accounts on the rep's patch that used to order and have gone quiet — the
 * prospecting list, and arguably the most actionable thing on the dashboard.
 *
 * Ordered by what the account USED to be worth, not by how long it has been
 * silent: a big account six weeks quiet is today's phone call, a small one
 * quiet for two years is not. The server owns that ordering.
 *
 * tel: and mailto: links are deliberate — this widget's whole purpose is to
 * start a conversation, and on a phone that should be one tap.
 *
 * SNOOZE. Some quiet accounts are known and dealt with — sold the herd, buys in
 * spring — and left at the top of the list they bury the ones that still need a
 * call. Each row can be snoozed for 1/3/6/12 months, to a date, or for good, and
 * the Snoozed tab holds them with an Unsnooze. A snooze is PER PERSON: it hides
 * the account from the caller's list only, never from a colleague's, even one
 * on the same rep. The server applies it after its shared per-rep cache.
 *
 * Snooze and unsnooze are optimistic — the row leaves at once — and reconciled
 * by re-reading the list once the server has answered. A refusal puts the row
 * back and says why.
 */

import React, { useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import ContactModal from '../components/ContactModal';
import { showAlert } from '../components/Alert';
import { API_URL } from '../config';
import useRepDashboard, { formatTonnes } from './useRepDashboard';
import RepRefreshButton from './RepRefreshButton';
import RepSnoozeMenu from './RepSnoozeMenu';
import { formatSnoozeUntil } from '../utils/snoozeDates';
import './RepWidgets.css';

/** "6 weeks", "4 months" — a rep thinks in weeks up to a point, then months. */
const humaniseGap = (days) => {
  if (!Number.isFinite(days)) return '';
  if (days < 14) return `${days} day${days === 1 ? '' : 's'}`;
  if (days < 84) return `${Math.round(days / 7)} weeks`;
  return `${Math.round(days / 30)} months`;
};

/*
 * With "ago" attached, and honest about the recent end. Gone Quiet rows are six
 * weeks at least, but a snoozed account may have ordered yesterday.
 */
const ago = (days) => {
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  return `${humaniseGap(days)} ago`;
};

const STEP = 10;
const WEEKS = 6;

/** PUT (until) or DELETE one of the caller's snoozes. Throws a sentence. */
async function sendSnooze(accountId, method, until) {
  const res = await fetch(`${API_URL}/api/rep-dashboard/lapsed-accounts/${accountId}/snooze`, {
    method,
    credentials: 'include',
    ...(method === 'PUT'
      ? { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ until }) }
      : {})
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.detail || body.error || 'the server did not accept it');
  return body;
}

/*
 * Call and email, in the same two slots on every row whether or not the account
 * has a number or an address — disabled rather than missing, so the snooze
 * button beside them stays in one column down the list.
 */
function ContactActions({ account }) {
  const phone = account.mobile || account.phone;
  return (
    <>
      {phone ? (
        <a className="btn-icon" href={`tel:${phone.replace(/\s+/g, '')}`} title={`Call ${phone}`}>
          <span className="material-symbols-rounded">call</span>
        </a>
      ) : (
        <button type="button" className="btn-icon btn-disabled" disabled title="No phone number on this account">
          <span className="material-symbols-rounded">call</span>
        </button>
      )}
      {account.email ? (
        <a className="btn-icon" href={`mailto:${account.email}`} title={`Email ${account.email}`}>
          <span className="material-symbols-rounded">mail</span>
        </a>
      ) : (
        <button type="button" className="btn-icon btn-disabled" disabled title="No email address on this account">
          <span className="material-symbols-rounded">mail</span>
        </button>
      )}
    </>
  );
}

function RepLapsedAccountsWidget({ variant = 'desktop' }) {
  const [tab, setTab] = useState('quiet');

  // Ten at a time, growing on demand — the same shape as Top Customers, and for
  // the same reason: the head of the list is what gets acted on.
  const [shown, setShown] = useState(STEP);
  const quiet = useRepDashboard('lapsed-accounts', {
    params: { weeks: WEEKS, limit: Math.min(shown + 1, 200) }
  });
  // Live and per person, so it is fetched each time the tab is opened.
  const snoozed = useRepDashboard('lapsed-accounts', {
    params: { weeks: WEEKS, view: 'snoozed' },
    skip: tab !== 'snoozed'
  });
  const [openAccountId, setOpenAccountId] = useState(null);

  /*
   * What this person has done to an account in this session, ahead of the
   * server's lists catching up: accountId -> true (snoozed) | false (unsnoozed).
   *
   * Kept for the life of the widget rather than cleared when a reload lands.
   * Each entry is the last thing this person asked for on that account, and is
   * rolled back if the server refuses it. Nobody else can change a person's
   * snoozes, so an entry cannot go stale against a refetch the way it could on
   * a shared record.
   */
  const [overrides, setOverrides] = useState(() => new Map());
  const setOverride = (accountId, value) => setOverrides((prev) => {
    const next = new Map(prev);
    if (value === undefined) next.delete(accountId);
    else next.set(accountId, value);
    return next;
  });

  // The async handlers below outlive the render that created them; they reach
  // the hooks through this so they see them as they are now, not as they were.
  const latest = useRef({ quiet, snoozed });
  latest.current = { quiet, snoozed };

  /*
   * The Snoozed tab's count: the list's own figure, unless a write has changed
   * it since that list arrived. The adjustment remembers WHICH list it was made
   * against, so the reload that follows a write replaces it without an effect
   * having to notice.
   */
  const [countAdjust, setCountAdjust] = useState(null);
  const countFrom = (adjust, data) =>
    (adjust && adjust.basis === data ? adjust.value : data?.snoozedCount);
  const serverCount = countFrom(countAdjust, quiet.data);
  const snoozedCount = Number.isFinite(serverCount) ? serverCount : null;
  const adjustCount = (fn) => setCountAdjust((prev) => {
    const data = latest.current.quiet.data;
    const now = countFrom(prev, data);
    return { basis: data, value: Math.max(0, fn(Number.isFinite(now) ? now : 0)) };
  });

  const all = quiet.data?.accounts || [];
  const visible = all.filter((a) => overrides.get(a.accountId) !== true);
  const rows = visible.slice(0, shown);
  // `total` is the length after the server removed this person's snoozes; the
  // rows snoozed here since are still in `all` until the reload lands.
  const quietTotal = quiet.data
    ? Math.max(0, (quiet.data.total ?? all.length) - (all.length - visible.length))
    : null;
  const hasMore = (quietTotal ?? visible.length) > rows.length;

  const snoozedRows = (snoozed.data?.accounts || [])
    .filter((a) => overrides.get(a.accountId) !== false);

  // The window the server measured tonnesPrior over: the priorMonths before the
  // quiet period began, not the last N months to today.
  const months = quiet.data?.priorMonths;
  const priorLabel = !months ? 'the prior period'
    : months === 12 ? 'the year before that'
    : `the ${months} months before that`;

  const snooze = async (account, until) => {
    const id = account.accountId;
    const before = overrides.get(id);
    setOverride(id, true);
    adjustCount((n) => n + 1);
    try {
      const body = await sendSnooze(id, 'PUT', until);
      if (Number.isFinite(body.snoozedCount)) adjustCount(() => body.snoozedCount);
      showAlert(
        body.snoozedUntil
          ? `${account.companyName} snoozed ${formatSnoozeUntil(body.snoozedUntil)} — it is under Snoozed`
          : `${account.companyName} snoozed until you unsnooze it — it is under Snoozed`,
        'success'
      );
      // Refills the page past the row that left, and corrects the counts.
      latest.current.quiet.reload();
    } catch (err) {
      setOverride(id, before);
      adjustCount((n) => n - 1);
      showAlert(`Could not snooze ${account.companyName}: ${err.message}`, 'error');
    }
  };

  const unsnooze = async (account) => {
    const id = account.accountId;
    const before = overrides.get(id);
    setOverride(id, false);
    adjustCount((n) => n - 1);
    try {
      const body = await sendSnooze(id, 'DELETE');
      if (Number.isFinite(body.snoozedCount)) adjustCount(() => body.snoozedCount);
      // Back on Gone Quiet, if it is still quiet.
      latest.current.quiet.reload();
    } catch (err) {
      setOverride(id, before);
      adjustCount((n) => n + 1);
      showAlert(`Could not unsnooze ${account.companyName}: ${err.message}`, 'error');
    }
  };

  const active = tab === 'quiet' ? quiet : snoozed;

  return (
    <div className="dashboard-card rep-widget rep-lapsed-card">
      <div className="card-header">
        <h3>
          <span className="material-symbols-rounded">phone_missed</span>
          Gone Quiet
        </h3>
        <div className="header-controls">
          {tab === 'quiet' && quiet.data && (
            <span className="rep-widget-sub">no order in {quiet.data.weeks} weeks</span>
          )}
          {tab === 'snoozed' && <span className="rep-widget-sub">hidden for you only</span>}
          <RepRefreshButton
            cachedAt={tab === 'quiet' ? quiet.data?.cachedAt : null}
            loading={active.loading}
            onRefresh={tab === 'quiet' ? quiet.refresh : snoozed.reload}
          />
        </div>
      </div>

      {/* The shared dashboard widget tabs (DashboardHome.css), as Standing
          Orders uses them. */}
      <div className="day-tabs rep-lapsed-tabs" role="tablist" aria-label="Gone Quiet lists">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'quiet'}
          className={`day-tab${tab === 'quiet' ? ' active' : ''}`}
          onClick={() => setTab('quiet')}
        >
          Gone Quiet
          {quietTotal !== null && <span className="tab-count">{quietTotal}</span>}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'snoozed'}
          className={`day-tab${tab === 'snoozed' ? ' active' : ''}`}
          onClick={() => setTab('snoozed')}
        >
          Snoozed
          {snoozedCount !== null && <span className="tab-count">{snoozedCount}</span>}
        </button>
      </div>

      <div className="card-body">
        {/* Loading only replaces the list when there is no list yet. A reload
            after a snooze keeps the rows in place instead of flashing to
            "Loading…" under the pointer. */}
        {active.loading && !active.data && <div className="rep-widget-state">Loading…</div>}
        {!active.loading && active.message && <div className="rep-widget-state">{active.message}</div>}

        {tab === 'quiet' && quiet.data && !quiet.message && (
          <>
            {rows.length === 0 && (
              <div className="rep-widget-state rep-widget-good">
                <span className="material-symbols-rounded">check_circle</span>
                {snoozedCount > 0
                  ? 'Everyone you have not snoozed has ordered recently.'
                  : 'Everyone who orders has ordered recently.'}
              </div>
            )}
            {rows.length > 0 && (
              <ul className="rep-lapsed-list">
                {rows.map(a => (
                  <li key={a.accountId} className="rep-lapsed-row">
                    <button
                      type="button"
                      className="rep-lapsed-main"
                      onClick={() => setOpenAccountId(a.accountId)}
                      title={`Open ${a.companyName}`}
                    >
                      <span className="rep-lapsed-name">{a.companyName}</span>
                      <span className="rep-lapsed-meta">
                        <span className="rep-lapsed-gap">{ago(a.daysSince)}</span>
                        {/* "was 621 T" is meaningless without the window it was
                            spread over — 621 T in a year and 621 T in a month are
                            very different customers. */}
                        <span className="rep-lapsed-was">
                          {formatTonnes(a.tonnesPrior)} over {priorLabel}
                        </span>
                      </span>
                    </button>
                    <span className="rep-lapsed-actions">
                      <ContactActions account={a} />
                      <RepSnoozeMenu
                        accountName={a.companyName}
                        onSnooze={(until) => snooze(a, until)}
                      />
                    </span>
                  </li>
                ))}
              </ul>
            )}
            {hasMore && (
              <button
                type="button"
                className="rep-show-more"
                onClick={() => setShown(n => n + STEP)}
                disabled={quiet.loading}
              >
                <span className="material-symbols-rounded">expand_more</span>
                Show {STEP} more
              </button>
            )}
          </>
        )}

        {tab === 'snoozed' && snoozed.data && !snoozed.message && (
          <>
            {snoozedRows.length === 0 && (
              <div className="rep-widget-state">
                Nothing snoozed. Snooze a row on Gone Quiet to hide it from your list for a while.
              </div>
            )}
            {snoozedRows.length > 0 && (
              <ul className="rep-lapsed-list">
                {snoozedRows.map(a => (
                  <li key={a.accountId} className="rep-lapsed-row rep-lapsed-row-snoozed">
                    <button
                      type="button"
                      className="rep-lapsed-main"
                      onClick={() => setOpenAccountId(a.accountId)}
                      title={`Open ${a.companyName}`}
                    >
                      <span className="rep-lapsed-name">{a.companyName}</span>
                      <span className="rep-lapsed-meta">
                        <span className="rep-lapsed-gap rep-lapsed-until">
                          {formatSnoozeUntil(a.snoozedUntil)}
                        </span>
                        <span className="rep-lapsed-was">
                          {a.daysSince === null ? 'never ordered' : `last order ${ago(a.daysSince)}`}
                        </span>
                      </span>
                    </button>
                    <span className="rep-lapsed-actions">
                      <ContactActions account={a} />
                      <button
                        type="button"
                        className="btn-icon"
                        onClick={() => unsnooze(a)}
                        title={`Unsnooze ${a.companyName} — back on your Gone Quiet list`}
                        aria-label={`Unsnooze ${a.companyName}`}
                      >
                        <span className="material-symbols-rounded">notifications_active</span>
                      </button>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>

      {openAccountId && ReactDOM.createPortal(
        <ContactModal contactId={openAccountId} onClose={() => setOpenAccountId(null)} />,
        document.body
      )}
    </div>
  );
}

export default RepLapsedAccountsWidget;
